import { useEffect, useState, useCallback } from "react";
import { QUEUE_EVENT, listQueuedChecks, flushCheckQueue } from "../services/fieldOffline";

const RETRY_MS = 30000;

// Keeps offline-queued well checks moving: tries to upload on login, the
// moment the browser reports it's back online, and every 30s while there
// is anything waiting. Returns the queue for the banner.
export function useFieldQueue(token) {
  const [queue, setQueue] = useState([]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  const refresh = useCallback(async () => setQueue(await listQueuedChecks()), []);
  const flush = useCallback(() => (token ? flushCheckQueue(token).catch(() => {}) : Promise.resolve()), [token]);

  useEffect(() => {
    refresh();
    window.addEventListener(QUEUE_EVENT, refresh);
    return () => window.removeEventListener(QUEUE_EVENT, refresh);
  }, [refresh]);

  useEffect(() => {
    const up = () => {
      setOnline(true);
      flush();
    };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    flush();
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, [flush]);

  const pendingCount = queue.filter((c) => c.status === "pending").length;
  useEffect(() => {
    if (pendingCount === 0) return undefined;
    const id = setInterval(flush, RETRY_MS);
    return () => clearInterval(id);
  }, [pendingCount, flush]);

  return { queue, pendingCount, failed: queue.filter((c) => c.status === "failed"), online, flush };
}
